import React, { FormEvent, useState } from 'react'
import classNames from 'classnames'

import { useMutationObserver } from 'shared/hooks/useMutationObserver'

import './DivInput.scss'

interface DivInputProps {
  innerRef: React.RefObject<HTMLDivElement>
  content: string
  onInput: (e: FormEvent<HTMLDivElement>) => void
  onBlur: (e: FormEvent<HTMLDivElement>) => void
  onClick: (e: FormEvent<HTMLDivElement>) => void
  onFocus?: (e: FormEvent<HTMLDivElement>) => void
  placeholder?: string
  className?: string
}

export const DivInput = ({
  innerRef,
  content,
  className,
  onFocus,
  onBlur,
  onInput,
  placeholder,
  ...rest
}: DivInputProps): JSX.Element => {
  const classes = classNames('div-input-text', className)
  const [isFocused, setFocus] = useState(false)

  const handleANode = (n) => {
    if (n.childNodes.length > 1 || n.childNodes[0].nodeName !== '#text') {
      n.parentElement.removeChild(n)
    }
  }

  const handleFocus = (e?: FormEvent<HTMLDivElement>) => {
    setFocus(true)

    if (onFocus) {
      onFocus(e)
    }
  }

  const handleBlur = (e: FormEvent<HTMLDivElement>) => {
    setFocus(false)

    if (onBlur) {
      onBlur(e)
    }
  }

  const insertNodeOnCursorPosition = (node: Node) => {
    const selection = window.getSelection()
    if (!selection.rangeCount) return
    selection.deleteFromDocument()
    selection.getRangeAt(0).insertNode(node)
    selection.collapseToEnd()
  }

  const onPaste = (e) => {
    e.preventDefault()

    const text = e.clipboardData.getData('text/plain')

    insertNodeOnCursorPosition(document.createTextNode(text))
    const event = new Event('input', {
      bubbles: true,
      cancelable: true,
    })

    innerRef.current.dispatchEvent(event)
  }

  useMutationObserver(
    innerRef,
    (records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((n: Node) => {
          switch (n.nodeName) {
            case 'A':
              handleANode(n)
              break
            case '#text':
            case 'BR':
            case 'DIV':
              return
            default:
              n.parentElement.removeChild(n)
          }
        })
      })
    },
    { childList: true, subtree: true }
  )
  return (
    <div className='div-input'>
      {!isFocused && content.length === 0 && placeholder && (
        <div
          onClick={() => {
            handleFocus()
            innerRef?.current?.focus()
          }}
          className='div-input-placeholder'
        >
          {placeholder}
        </div>
      )}
      <div
        ref={innerRef}
        contentEditable='true'
        className={classes}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onPaste={onPaste}
        onInput={onInput}
        dangerouslySetInnerHTML={{ __html: content }}
        {...{ ...rest }}
      ></div>
    </div>
  )
}
